import { useState, useEffect, useCallback } from 'react';
import { getAccessToken } from '../api/client';
import { Button } from '../components/Button';

// ============================================================
// Historial de cambios
// ============================================================
interface HistorialRow {
  id: number;
  mlaId: string;
  variantId: string | null;
  fecha: string;
  precioAnterior: number | null;
  precioNuevo: number | null;
  stockAnterior: number | null;
  stockNuevo: number | null;
  statusAnterior: string | null;
  statusNuevo: string | null;
  origen: string | null;
  usuario?: string | null;
  titulo: string | null;
  skuInterno: string | null;
}

const STATUS_LABEL: Record<string, string> = {
  active: 'Activa',
  paused: 'Pausada',
  closed: 'Cerrada',
  under_review: 'En revisión',
};

function statusBadge(s: string | null) {
  if (!s) return null;
  const colors: Record<string, string> = {
    active: 'bg-emerald-100 text-emerald-700',
    paused: 'bg-amber-100 text-amber-700',
    closed: 'bg-red-100 text-red-700',
  };
  const cls = colors[s] ?? 'bg-slate-100 text-slate-600';
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{STATUS_LABEL[s] ?? s}</span>;
}

function fmtPrecio(p: number | null) {
  if (p == null) return '?';
  return '$' + p.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

const ORIGEN_CFG: Record<string, { label: string; cls: string }> = {
  venta:         { label: '🛒 Venta',         cls: 'bg-blue-100 text-blue-700' },
  manual:        { label: '✏️ Manual',         cls: 'bg-slate-100 text-slate-600' },
  sync:          { label: '🔗 Sync',           cls: 'bg-teal-100 text-teal-700' },
  precio_manual: { label: '💰 Precio manual',  cls: 'bg-purple-100 text-purple-700' },
  activada:      { label: '▶️ Activada',        cls: 'bg-emerald-100 text-emerald-700' },
  pausada:       { label: '⏸️ Pausada',         cls: 'bg-amber-100 text-amber-700' },
  pausa_manual:  { label: 'Pausada a mano',    cls: 'bg-amber-100 text-amber-700' },
  activacion_manual: { label: 'Activada a mano', cls: 'bg-emerald-100 text-emerald-700' },
  estado:        { label: '🔄 Estado',          cls: 'bg-slate-100 text-slate-600' },
};

/** Envíos de la sincronización por SKU: origen `sync_sku:<motivo>` (ver backend ml-sync-sku.alcance.ts). */
const MOTIVO_SYNC_SKU: Record<string, string> = {
  venta: 'venta', cancelacion: 'cancelación', factura: 'factura', despacho: 'despacho de venta', ingreso: 'ingreso', ajuste: 'ajuste',
  conciliacion: 'conciliación', activacion: 'activación', verificacion: 'conteo del stock de resguardo', inventario: 'conteo de inventario', reenvio: 'reenvío a mano',
};

function origenBadge(o: string | null, usuario?: string | null) {
  if (!o) return <span className="text-slate-300 text-xs">—</span>;
  const motivoSync = o.startsWith('sync_sku:') ? o.slice('sync_sku:'.length) : null;
  // `sync_sku:<motivo>:deposito`: stock de depósito propio de una publicación Full.
  const deposito = motivoSync?.endsWith(':deposito') ?? false;
  const motivo = deposito && motivoSync ? motivoSync.slice(0, -':deposito'.length) : motivoSync;
  const cfg = ORIGEN_CFG[o]
    ?? (motivo !== null
      ? { label: `🔁 Sync por SKU · ${MOTIVO_SYNC_SKU[motivo] ?? motivo}${deposito ? ' · stock de depósito (Full)' : ''}`, cls: 'bg-indigo-100 text-indigo-700' }
      : null)
    ?? { label: o, cls: 'bg-slate-100 text-slate-600' };
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>{cfg.label}{usuario ? ` por ${usuario}` : ''}</span>;
}

export function HistorialMl({ sku }: { sku?: string } = {}) {
  const [mlaSearch, setMlaSearch] = useState('');
  const [tituloSearch, setTituloSearch] = useState('');
  const [tituloApplied, setTituloApplied] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(0);
  const [exporting, setExporting] = useState(false);
  const PAGE_SIZE = sku ? 20 : 100;
  const [rows, setRows] = useState<HistorialRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const buildParams = useCallback((p: number, extra?: Record<string, string>) => {
    const params = new URLSearchParams();
    if (sku) params.set('sku', sku);
    if (mlaSearch) params.set('mlaId', mlaSearch.trim());
    if (tituloApplied) params.set('titulo', tituloApplied.trim());
    if (from) params.set('from', from);
    if (to)   params.set('to', to + 'T23:59:59');
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String(p * PAGE_SIZE));
    if (extra) Object.entries(extra).forEach(([k, v]) => params.set(k, v));
    return params;
  }, [sku, PAGE_SIZE, mlaSearch, tituloApplied, from, to]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const token = getAccessToken();
      const params = buildParams(0, { export: 'csv', limit: '5000' });
      const response = await window.fetch(`/api/v3/ml/historial?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'historial-ml.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      alert('Error al exportar: ' + e.message);
    } finally {
      setExporting(false);
    }
  }, [buildParams]);

  const fetchWithHeader = useCallback(async (p: number) => {
    setLoading(true);
    setError('');
    try {
      const params = buildParams(p);

      const token = getAccessToken();
      const response = await window.fetch(`/api/v3/ml/historial?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (!response.ok) {
        const body = await response.text().catch(() => '');
        throw new Error(`HTTP ${response.status}: ${body.slice(0, 200)}`);
      }
      const data: HistorialRow[] = await response.json();
      const totalHeader = response.headers.get('X-Total-Count');
      setRows(data);
      setTotal(totalHeader ? parseInt(totalHeader, 10) : data.length);
    } catch(e: any) {
      setError('Error al cargar historial: ' + (e?.message ?? String(e)));
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => { fetchWithHeader(0); setPage(0); }, [sku, mlaSearch, tituloApplied, from, to]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages = Math.ceil(total / PAGE_SIZE);

  function fmtFecha(f: string) {
    const d = new Date(f);
    return d.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  return (
    <div className="space-y-4">
      {/* Filtros */}
      {!sku && <div className="flex flex-wrap gap-3 items-end">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-slate-500 font-medium">MLA o SKU</label>
          <input
            value={mlaSearch}
            onChange={e => setMlaSearch(e.target.value)}
            placeholder="Ej: MLA1455083070"
            className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm w-44 focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-slate-500 font-medium">Título de publicación</label>
          <div className="flex gap-1">
            <input
              value={tituloSearch}
              onChange={e => setTituloSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { setTituloApplied(tituloSearch); setPage(0); } }}
              placeholder="Ej: Resaltador Filgo"
              className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm w-44 focus:outline-none focus:ring-2 focus:ring-brand-400"
            />
            <button
              onClick={() => { setTituloApplied(tituloSearch); setPage(0); }}
              className="border border-slate-300 hover:border-brand-400 rounded-lg px-2.5 text-slate-500 hover:text-brand-700 text-sm transition-colors"
              title="Buscar">
              🔍
            </button>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-slate-500 font-medium">Desde</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-slate-500 font-medium">Hasta</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
        </div>
        {(mlaSearch || tituloApplied || from || to) && (
          <button onClick={() => { setMlaSearch(''); setTituloSearch(''); setTituloApplied(''); setFrom(''); setTo(''); }}
            className="text-sm text-slate-500 hover:text-red-600 border border-slate-200 px-3 py-1.5 rounded-lg hover:border-red-300 transition-colors">
            ✕ Limpiar
          </button>
        )}
        <div className="ml-auto flex items-end gap-3">
          <button onClick={handleExport} disabled={exporting}
            className="flex items-center gap-1.5 text-sm border border-slate-300 hover:border-brand-400 px-3 py-1.5 rounded-lg hover:text-brand-700 transition-colors disabled:opacity-50">
            {exporting ? '⏳ Exportando…' : '⬇ Exportar CSV'}
          </button>
          <span className="text-sm text-slate-400">
            {total > 0 ? `${total.toLocaleString('es-AR')} cambios registrados` : ''}
          </span>
        </div>
      </div>}

      {error && <div className="bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm text-red-700">{error}</div>}

      {total === 0 && !loading && !error && (sku ? (
        <p className="text-sm text-slate-500">No hay cambios registrados en Mercado Libre para este SKU.</p>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center text-amber-800 text-sm">
          <p className="font-semibold mb-1">Aún no hay historial registrado</p>
          <p className="text-amber-600">El sistema empieza a registrar cambios desde el próximo ciclo de sync (cada 1 minuto). Los cambios de precio, stock y estado aparecerán aquí automáticamente.</p>
        </div>
      ))}

      {(loading && rows.length === 0) && (
        <div className="text-center py-10 text-slate-400 text-sm">Cargando historial…</div>
      )}

      {rows.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left">
                  <th className="px-4 py-2.5 font-semibold text-slate-600 text-xs">Fecha</th>
                  <th className="px-4 py-2.5 font-semibold text-slate-600 text-xs">MLA</th>
                  <th className="px-4 py-2.5 font-semibold text-slate-600 text-xs">SKU</th>
                  <th className="px-4 py-2.5 font-semibold text-slate-600 text-xs">Publicación</th>
                  <th className="px-4 py-2.5 font-semibold text-slate-600 text-xs">Origen</th>
                  <th className="px-4 py-2.5 font-semibold text-slate-600 text-xs text-right">Precio</th>
                  <th className="px-4 py-2.5 font-semibold text-slate-600 text-xs text-right">Stock</th>
                  <th className="px-4 py-2.5 font-semibold text-slate-600 text-xs">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map(r => (
                  <tr key={r.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap text-xs">{fmtFecha(r.fecha)}</td>
                    <td className="px-4 py-2.5">
                      <a href={`https://articulo.mercadolibre.com.ar/-_JM-${r.mlaId.replace('MLA','')}`} target="_blank" rel="noreferrer"
                        className="text-brand-600 hover:text-brand-800 font-mono text-xs font-medium">
                        {r.mlaId}
                      </a>
                      {r.variantId && <span className="ml-1 text-slate-400 text-xs">#{r.variantId}</span>}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-slate-500 text-xs whitespace-nowrap">{r.skuInterno ?? '—'}</td>
                    <td className="px-4 py-2.5 text-slate-700 max-w-xs truncate text-xs" title={r.titulo ?? ''}>{r.titulo ?? '—'}</td>
                    <td className="px-4 py-2.5">{origenBadge(r.origen, r.usuario)}</td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      {r.precioNuevo != null ? (
                        <span className="inline-flex items-center gap-1 text-xs">
                          <span className="text-slate-400">{fmtPrecio(r.precioAnterior)}</span>
                          <span className="text-slate-400">→</span>
                          <span className={`font-semibold ${(r.precioNuevo ?? 0) > (r.precioAnterior ?? 0) ? 'text-emerald-600' : 'text-red-600'}`}>
                            {fmtPrecio(r.precioNuevo)}
                          </span>
                        </span>
                      ) : <span className="text-slate-300 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      {r.stockNuevo != null ? (
                        <span className="inline-flex items-center gap-1 text-xs">
                          <span className="text-slate-400">{r.stockAnterior ?? '?'}</span>
                          <span className="text-slate-400">→</span>
                          <span className={`font-semibold ${(r.stockNuevo ?? 0) > (r.stockAnterior ?? 0) ? 'text-emerald-600' : 'text-red-600'}`}>
                            {r.stockNuevo}
                          </span>
                        </span>
                      ) : <span className="text-slate-300 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      {r.statusNuevo ? (
                        <span className="inline-flex items-center gap-1 text-xs">
                          <span className="text-slate-400">{STATUS_LABEL[r.statusAnterior ?? ''] ?? r.statusAnterior ?? '?'}</span>
                          <span className="text-slate-400">→</span>
                          {statusBadge(r.statusNuevo)}
                        </span>
                      ) : <span className="text-slate-300 text-xs">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && !sku && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
              <span className="text-sm text-slate-500">
                {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} de {total.toLocaleString('es-AR')}
              </span>
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" disabled={page === 0 || loading} onClick={() => { const p = page - 1; setPage(p); fetchWithHeader(p); }}>← Anterior</Button>
                <Button variant="secondary" size="sm" disabled={page >= totalPages - 1 || loading} onClick={() => { const p = page + 1; setPage(p); fetchWithHeader(p); }}>Siguiente →</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
